import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";

const SITE_URL = "https://www.venturapowerwashing.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

interface SEOProps {
  title: string;
  description?: string;
  /** Path of this page, e.g. "/services/pressure-washing" — used for canonical + og:url. */
  path: string;
  keywords?: string;
  image?: string;
  /** Auth/admin pages: emit robots noindex instead of canonical + OG/Twitter tags. */
  noindex?: boolean;
  /** Extra head tags (JSON-LD scripts, etc.) rendered alongside the standard ones. */
  children?: ReactNode;
}

// Every public page was setting title/description/canonical individually and
// none set Open Graph or Twitter Card tags, so sharing any page but the
// homepage produced a link preview with the homepage's title/image. Centralize
// that here instead of repeating it ~20 times.
export function SEO({ title, description, path, keywords, image, noindex, children }: SEOProps) {
  const url = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  // react-helmet-async inspects Helmet's children directly and drops
  // anything nested inside a Fragment — every tag must be a direct child,
  // not grouped with <>...</>, or everything after the first one silently
  // vanishes from the rendered head.
  return (
    <Helmet>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <link rel="canonical" href={url} />}
      {!noindex && <meta property="og:title" content={title} />}
      {!noindex && description && <meta property="og:description" content={description} />}
      {!noindex && <meta property="og:url" content={url} />}
      {!noindex && <meta property="og:type" content="website" />}
      {!noindex && <meta property="og:image" content={ogImage} />}
      {!noindex && <meta name="twitter:card" content="summary_large_image" />}
      {!noindex && <meta name="twitter:title" content={title} />}
      {!noindex && description && <meta name="twitter:description" content={description} />}
      {!noindex && <meta name="twitter:image" content={ogImage} />}
      {children}
    </Helmet>
  );
}
